import Swiper from 'swiper';
import {
  Pagination,
  EffectFade,
  Autoplay,
  EffectCreative,
  A11y,
} from 'swiper/modules';

export default function initSwiper() {
  const HERO_AUTOPLAY_DELAY = 10000;
  const MOBILE_BREAKPOINT = 1024;
  const A11Y_APPLIED_EVENT = 'white:a11y-applied';
  const quoteSwipers: Swiper[] = [];

  /**
   * Updates the `tabindex` attributes of all slides in a Swiper instance to ensure
   * that only the current slide and its relevant elements are focusable.
   *
   * This function is used to improve accessibility by making non-active slides
   * and their interactive elements (e.g., buttons, titles) non-focusable, while
   * ensuring the active slide and its elements are focusable.
   *
   * @param {Swiper} slider - The Swiper instance whose slides need to be updated.
   */
  function makeAllButCurrentSlideTabable(slider: Swiper) {
    // Get the currently active slide element
    const currentSlideEl = slider.slides[slider.realIndex];

    // Iterate over all slides in the Swiper instance
    slider.slides.forEach((slide) => {
      if (slide !== currentSlideEl) {
        // For non-active slides, set `tabindex` to -1 to make them non-focusable
        slide.setAttribute('tabindex', '-1');

        // Update all buttons within the slide to be non-focusable
        slide.querySelectorAll('.slide-btn').forEach((btn) => {
          btn.setAttribute('tabindex', '-1');
        });

        // Remove `tabindex` from all titles within the slide
        slide.querySelectorAll('.slide-title').forEach((el) => {
          el.removeAttribute('tabindex');
        });
      } else {
        // For the active slide, remove the `tabindex` attribute to make it focusable
        slide.removeAttribute('tabindex');

        // Update all buttons within the active slide to be focusable
        slide.querySelectorAll('.slide-btn').forEach((btn) => {
          btn.removeAttribute('tabindex');
        });

        // Set `tabindex` to 0 for all titles within the active slide to make them focusable
        slide.querySelectorAll('.slide-title').forEach((el) => {
          el.setAttribute('tabindex', '0');
        });
      }
    });
  }

  /**
   * Resets the progress animation of a given progress circle element.
   *
   * This function toggles the `animationName` property of the provided
   * progress circle element between two predefined animation names
   * (`progress1` and `progress2`). This is useful for restarting the
   * animation sequence when needed.
   *
   * @param {HTMLElement} progressCircle - The progress circle whose animation needs to be reset.
   */
  function resetProgressAnimation(progressCircle: HTMLElement) {
    if (progressCircle.style.animationName === 'progress1') {
      // eslint-disable-next-line no-param-reassign
      progressCircle.style.animationName = 'progress2';
    } else {
      // eslint-disable-next-line no-param-reassign
      progressCircle.style.animationName = 'progress1';
    }
  }

  /**
   * Enhances the usability of the pagination bullets in a Swiper instance.
   *
   * This function adds keyboard and click event listeners to the pagination bullets
   * of a Swiper slider. It ensures that pressing the Space or Enter key on a bullet
   * triggers the appropriate action, and clicking a bullet focuses on the corresponding
   * slide's title for better accessibility.
   *
   * @param {Swiper} slider - The Swiper instance whose pagination usability is being initialized.
   */
  function initPaginationUsability(slider: Swiper) {
    const pagination = slider.el.querySelectorAll('.swiper-pagination-bullet');

    pagination.forEach((item: Element, index) => {
      // Add a keydown event listener to handle Space key presses
      item.addEventListener('keydown', (event) => {
        const keyboardEvent = event as KeyboardEvent;
        if (keyboardEvent.code === 'Space') {
          event.preventDefault(); // Prevent default scrolling behavior
        }
      });

      // Add a click event listener to focus on the corresponding slide's title
      item.addEventListener('click', (e) => {
        e.preventDefault(); // Prevent default click behavior
        const heroTitle = slider.slides[index].querySelector('.slide-title') as HTMLElement;
        if (heroTitle) {
          // Wait for the DOM to update before focusing on the slide title
          setTimeout(() => {
            heroTitle.focus();
          }, 20);
        }
      });
    });
  }

  /**
   * Updates the play/pause button for a Swiper instance based on the autoplay state.
   *
   * This function toggles the autoplay state of the Swiper instance and updates
   * the associated control button's attributes and visual state to reflect the
   * current playback status. It also ensures accessibility by updating the
   * `aria-pressed` attribute and screen reader text.
   *
   * @param {Swiper} slider - The Swiper instance whose autoplay state is being controlled.
   * @param {HTMLElement} controlBtn - The button element used to toggle play/pause.
   * @param {boolean} stop - Boolean indicating whether to stop/start (`true`/`false`) autoplay.
   */
  function updatePlayPauseBtn(slider: Swiper, controlBtn: HTMLElement, stop: boolean) {
    if (controlBtn) {
      // Get the screen reader text element inside the control button
      const srText = controlBtn.querySelector('.sr-only') as HTMLElement;
      const progressCircle = controlBtn.querySelector('.swiper-progress circle') as HTMLElement;

      if (stop) {
        // Stop autoplay and update button to reflect "Play" state
        slider.autoplay.stop();
        if (progressCircle) {
          progressCircle.style.animationPlayState = 'paused';
        }
        srText.textContent = srText.dataset.textPlay || 'Play';
        controlBtn.setAttribute('aria-pressed', 'true');
        controlBtn.querySelector('.play-icon')?.classList.remove('hidden');
        controlBtn.querySelector('.pause-icon')?.classList.add('hidden');
      } else {
        // Start autoplay and update button to reflect "Pause" state
        slider.autoplay.start();
        if (progressCircle) {
          resetProgressAnimation(progressCircle);
        }
        srText.textContent = srText.dataset.textPause || 'Pause';
        controlBtn.setAttribute('aria-pressed', 'false');
        controlBtn.querySelector('.play-icon')?.classList.add('hidden');
        controlBtn.querySelector('.pause-icon')?.classList.remove('hidden');
      }
    }
  }

  /**
   * Initializes the play/pause button functionality for a Swiper instance.
   *
   * This function adds an event listener to the play/pause button of the Swiper slider.
   * When the button is clicked, it toggles the autoplay state of the slider and updates
   * the button's attributes and visual state accordingly.
   *
   * @param {Swiper} slider - Swiper instance for which the play/pause button is being initialized.
   */
  function initPlayPauseButton(slider: Swiper) {
    const controlBtn = slider.el.querySelector('.swiper-btn-play') as HTMLElement;
    controlBtn.addEventListener('pointerup', () => {
      // Check if the button is currently in the "play" state
      if (controlBtn.getAttribute('aria-pressed') === 'false') {
        // Update the button to the "pause" state and stop autoplay
        updatePlayPauseBtn(slider, controlBtn, true);
      } else if (!controlBtn.classList.contains('js-focus-event')) {
        // If the button is not in a focus event state,
        // update it to the "play" state and start autoplay
        // This prevents autoplay from restarting if it was already paused due to a focus event
        updatePlayPauseBtn(slider, controlBtn, false);
      }

      // Remove the focus event class to reset the button's state
      controlBtn.classList.remove('js-focus-event');
    });
  }

  function refreshQuoteSwiperHeights() {
    // Wait for font/layout updates to settle before measuring slide heights.
    requestAnimationFrame(() => {
      quoteSwipers.forEach((slider) => {
        if (slider.destroyed) {
          return;
        }
        slider.update();
        slider.updateAutoHeight(0);
      });
    });
  }

  document.querySelectorAll<HTMLElement>('.swiper-about-us').forEach(
    ($mainSwiper) => new Swiper($mainSwiper, {
      modules: [Autoplay, Pagination, EffectFade, A11y],
      grabCursor: true,
      spaceBetween: 0,
      slidesPerView: 1,
      loop: true,
      // autoplay: {
      //   delay: 5000,
      //   pauseOnMouseEnter: true,
      // },
      effect: 'fade',
      pagination: {
        el: '.swiper-pagination',
        clickable: true,
      },
    }),
  );

  document.querySelectorAll<HTMLElement>('.swiper-hero').forEach(($mainSwiper) => {
    const swiper = new Swiper($mainSwiper, {
      modules: [Autoplay, Pagination, EffectFade, A11y],
      grabCursor: true,
      spaceBetween: 0,
      slidesPerView: 1,
      autoplay: {
        delay: HERO_AUTOPLAY_DELAY,
        pauseOnMouseEnter: true,
      },
      loop: true,
      autoHeight: false,
      effect: 'fade',
      pagination: {
        el: '.swiper-pagination',
        clickable: true,
      },
      breakpoints: {
        1024: {},
      },
      on: {
        init(slider) {
          makeAllButCurrentSlideTabable(slider);
        },
        afterInit(slider) {
          // Jump to the current active bullet on tab
          const button = slider.el.querySelectorAll('.slide-btn');
          button.forEach((item) => {
            item.addEventListener('keydown', (event) => {
              const keyboardEvent = event as KeyboardEvent;
              if (keyboardEvent.code === 'Tab' && !keyboardEvent.shiftKey) {
                event.preventDefault();
                // eslint-disable-next-line @stylistic/max-len
                const activeBullet = slider.el.querySelector('.swiper-pagination-bullet-active') as HTMLElement;
                activeBullet.focus();
              }
            });
          });

          initPaginationUsability(slider);

          initPlayPauseButton(slider);

          // start the progress circle animation
          // eslint-disable-next-line @stylistic/max-len
          const progressCircle = slider.el.querySelector('.swiper-btn-play .swiper-progress circle') as HTMLElement;
          progressCircle.style.animationName = 'progress1';
          progressCircle.style.animationDuration = `${HERO_AUTOPLAY_DELAY}ms`;
        },
        slideChange(slider) {
          makeAllButCurrentSlideTabable(slider);
        },
        autoplayPause(slider) {
          // eslint-disable-next-line @stylistic/max-len
          const progressCircle = slider.el.querySelector('.swiper-btn-play .swiper-progress circle') as HTMLElement;
          progressCircle.style.animationPlayState = 'paused';
        },
        autoplayResume(slider) {
          // eslint-disable-next-line @stylistic/max-len
          const progressCircle = slider.el.querySelector('.swiper-btn-play .swiper-progress circle') as HTMLElement;
          progressCircle.style.animationPlayState = 'running';
        },
        touchEnd(slider) {
          // autoplay delay resets when manually interacting so reset the progressCircle
          // but don't reset when swiping vertically (slider.progress is then either 0 or 1)
          if (!Number.isInteger(slider.progress)) {
            // eslint-disable-next-line @stylistic/max-len
            const progressCircle = slider.el.querySelector('.swiper-btn-play .swiper-progress circle') as HTMLElement;
            resetProgressAnimation(progressCircle);
          }
        },
        breakpoint(slider) {
          // don't autoplay on screens < MOBILE_BREAKPOINTpx
          if (window.innerWidth < MOBILE_BREAKPOINT) {
            slider.autoplay.stop();
          } else {
            slider.autoplay.start();
          }
        },
        autoplayStart(slider) {
          // don't autoplay on screens < MOBILE_BREAKPOINTpx
          if (window.innerWidth < MOBILE_BREAKPOINT) {
            slider.autoplay.stop();
          }
        },
      },
    });

    swiper.el.addEventListener('focusin', () => {
      // stop autoplaying the slider when accessing a slide with the keyboard
      const controlBtn = swiper.el.querySelector('.swiper-btn-play') as HTMLElement;
      if (controlBtn) {
        updatePlayPauseBtn(swiper, controlBtn, true);
        controlBtn.classList.add('js-focus-event');
      }
    });
  });

  document.querySelectorAll<HTMLElement>('.swiper-logos').forEach(
    ($mainSwiper) => new Swiper($mainSwiper, {
      modules: [Autoplay, A11y],
      grabCursor: true,
      slidesPerView: 'auto',
      loop: true,
      autoplay: {
        delay: 2500,
        pauseOnMouseEnter: true,
      },
      a11y: {
        scrollOnFocus: false,
      },
      breakpoints: {
        1024: {
          slidesPerView: 'auto', // breakpoint must be set to be triggered in "on breakpoint"
        },
        1520: {
          slidesPerView: parseInt(<string>$mainSwiper.dataset.slidesPerView, 10) ?? 5,
          spaceBetween: 128,
        },
      },
      on: {
        afterInit(slider) {
          // eslint-disable-next-line @stylistic/max-len
          if (slider.slides.length > (parseInt(<string>$mainSwiper.dataset.slidesPerView, 10) ?? 5)) {
            initPlayPauseButton(slider);
          }
        },
        breakpoint(slider) {
          // don't autoplay on screens < MOBILE_BREAKPOINTpx
          if (window.innerWidth < MOBILE_BREAKPOINT) {
            slider.autoplay.stop();
          } else {
            slider.autoplay.start();
          }
        },
        autoplayStart(slider) {
          // don't autoplay on screens < MOBILE_BREAKPOINTpx
          if (window.innerWidth < MOBILE_BREAKPOINT) {
            slider.autoplay.stop();
          }
        },
      },
    }),
  );

  document.querySelectorAll<HTMLElement>('.swiper-quotes-holder').forEach(($holder) => {
    const $mainSwiper = $holder.querySelector<HTMLElement>('.swiper-quotes');
    const $pagination = $holder.querySelector<HTMLElement>('.swiper-pagination');

    if ($mainSwiper) {
      const quoteSwiper = new Swiper($mainSwiper, {
        modules: [Pagination, A11y],
        slidesPerView: 'auto',
        grabCursor: true,
        spaceBetween: 24,
        loop: true,
        autoHeight: true,
        scrollbar: {
          el: '.swiper-scrollbar',
          draggable: true,
          hide: false,
        },
        pagination: {
          el: $pagination,
          clickable: true,
        },
        on: {
          init(slider) {
            makeAllButCurrentSlideTabable(slider);
          },
          afterInit(slider) {
            initPaginationUsability(slider);
          },
          slideChange(slider) {
            makeAllButCurrentSlideTabable(slider);
          },
        },
      });

      quoteSwipers.push(quoteSwiper);
    }
  });

  window.addEventListener(A11Y_APPLIED_EVENT, () => {
    refreshQuoteSwiperHeights();
  });

  document.querySelectorAll<HTMLElement>('.swiper-cards').forEach(
    ($mainSwiper) => new Swiper($mainSwiper, {
      modules: [A11y],
      slidesPerView: 'auto',
      grabCursor: true,
      spaceBetween: 16,
      breakpoints: {
        1024: {
          spaceBetween: 32,
        },
      },
    }),
  );

  document.querySelectorAll<HTMLElement>('.swiper-images').forEach(
    ($mainSwiper) => new Swiper($mainSwiper, {
      modules: [Pagination, EffectCreative, A11y],
      grabCursor: true,
      loop: true,
      effect: 'creative',
      creativeEffect: {
        prev: {
          shadow: true,
          translate: [0, 0, -400],
        },
        next: {
          translate: ['100%', 0, 0],
        },
      },
      pagination: {
        el: '.swiper-pagination',
        clickable: true,
      },
    }),
  );
}
